import {
  ORDER_GRID_SELECTION_COLUMN_WIDTH,
  resolveOrderGridColumnWidth,
} from "../hooks/useDataManagerOrdersUtils.js";

export const ORDER_GRID_SELECTION_COLUMN_KEY = "__selection__";

export const DATA_MANAGER_FROZEN_HEADER_CELL_CLASS_NAME = "bg-slate-100 ring-1 ring-inset ring-accent/30";
export const DATA_MANAGER_FROZEN_LAST_COLUMN_CLASS_NAME = "shadow-[4px_0_10px_-6px_rgba(15,23,42,0.28)]";
export const DATA_MANAGER_FROZEN_PIN_ICON_CLASS_NAME = "shrink-0 fill-accent/25 text-accent";
export const DATA_MANAGER_FROZEN_PIN_ICON_STROKE_WIDTH = 3;

const ORDER_GRID_TRAILING_COLUMN_KEYS = new Set([
  "dispatch_status",
  "billing_approval_status",
]);

export function dedupeOrderGridColumnsByKey(columns = []) {
  const seenKeys = new Set();
  const dedupedColumns = [];

  (Array.isArray(columns) ? columns : []).forEach((column) => {
    if (!column?.key || seenKeys.has(column.key)) {
      return;
    }

    seenKeys.add(column.key);
    dedupedColumns.push(column);
  });

  return dedupedColumns;
}

export function buildOrderGridFreezableColumnDescriptors({
  orderColumnWidths = null,
  resolvedOrderColumns = [],
  showSelectionColumn = false,
  trailingColumns = [],
}) {
  const descriptors = [];

  if (showSelectionColumn) {
    descriptors.push({
      key: ORDER_GRID_SELECTION_COLUMN_KEY,
      label: "Sélection",
      width: ORDER_GRID_SELECTION_COLUMN_WIDTH,
    });
  }

  (Array.isArray(resolvedOrderColumns) ? resolvedOrderColumns : [])
    .filter((column) => !ORDER_GRID_TRAILING_COLUMN_KEYS.has(column.key))
    .forEach((column) => {
    descriptors.push({
      key: column.key,
      label: column.label,
      width: resolveOrderGridColumnWidth(column.key, orderColumnWidths),
    });
  });

  const descriptorKeys = new Set(descriptors.map((column) => column.key));

  (Array.isArray(trailingColumns) ? trailingColumns : []).forEach((column) => {
    if (!column?.key || descriptorKeys.has(column.key)) {
      return;
    }

    descriptorKeys.add(column.key);
    descriptors.push({
      key: column.key,
      label: column.label,
      width: resolveOrderGridColumnWidth(column.key, orderColumnWidths),
    });
  });

  return descriptors;
}

export function normalizeFrozenColumnKeys(frozenColumnKeys, freezableColumnDescriptors = []) {
  const validKeys = new Set(
    freezableColumnDescriptors
      .filter((column) => column.key !== ORDER_GRID_SELECTION_COLUMN_KEY)
      .map((column) => column.key),
  );
  const normalizedKeys = [];
  const seenKeys = new Set();

  (Array.isArray(frozenColumnKeys) ? frozenColumnKeys : []).forEach((columnKey) => {
    const normalizedKey = typeof columnKey === "string" ? columnKey.trim() : "";
    if (!normalizedKey || !validKeys.has(normalizedKey) || seenKeys.has(normalizedKey)) {
      return;
    }

    seenKeys.add(normalizedKey);
    normalizedKeys.push(normalizedKey);
  });

  return normalizedKeys;
}

export function splitColumnsByFrozenState(resolvedOrderColumns = [], frozenColumnKeys = []) {
  const frozenColumnKeySet = new Set(frozenColumnKeys);
  const pinnedColumns = [];
  const scrollableColumns = [];

  (Array.isArray(resolvedOrderColumns) ? resolvedOrderColumns : []).forEach((column) => {
    if (frozenColumnKeySet.has(column.key)) {
      pinnedColumns.push(column);
    } else {
      scrollableColumns.push(column);
    }
  });

  return {
    pinnedColumns,
    scrollableColumns,
  };
}

export function buildPinnedSectionDescriptors({
  freezableColumnDescriptors = [],
  frozenColumnKeys = [],
  pinSelectionColumn = false,
}) {
  const frozenColumnKeySet = new Set(frozenColumnKeys);
  const pinnedDescriptors = [];
  const seenPinnedKeys = new Set();

  if (pinSelectionColumn) {
    const selectionDescriptor = freezableColumnDescriptors.find(
      (column) => column.key === ORDER_GRID_SELECTION_COLUMN_KEY,
    );
    if (selectionDescriptor) {
      pinnedDescriptors.push(selectionDescriptor);
      seenPinnedKeys.add(selectionDescriptor.key);
    }
  }

  freezableColumnDescriptors.forEach((column) => {
    if (column.key === ORDER_GRID_SELECTION_COLUMN_KEY) {
      return;
    }

    if (frozenColumnKeySet.has(column.key) && !seenPinnedKeys.has(column.key)) {
      pinnedDescriptors.push(column);
      seenPinnedKeys.add(column.key);
    }
  });

  return pinnedDescriptors;
}

export function buildFrozenColumnLayout(pinnedSectionDescriptors = []) {
  const layoutByKey = {};
  let leftOffset = 0;
  let lastPinnedColumnKey = null;

  pinnedSectionDescriptors.forEach((column) => {
    layoutByKey[column.key] = {
      isFrozen: true,
      isLastFrozen: false,
      left: leftOffset,
    };
    leftOffset += Math.max(0, Math.round(Number(column.width) || 0));
    lastPinnedColumnKey = column.key;
  });

  if (lastPinnedColumnKey && layoutByKey[lastPinnedColumnKey]) {
    layoutByKey[lastPinnedColumnKey].isLastFrozen = true;
  }

  return layoutByKey;
}

export function buildFrozenColumnCellStyle(layoutEntry, baseStyle = null, { isHeader = false } = {}) {
  if (!layoutEntry?.isFrozen) {
    return baseStyle;
  }

  return {
    ...(baseStyle && typeof baseStyle === "object" ? baseStyle : {}),
    left: `${layoutEntry.left}px`,
    position: "sticky",
    zIndex: isHeader ? 20 : 2,
  };
}

export function resolveFrozenOrderCellBackgroundClassName({
  hasDraft = false,
  isEditing = false,
  isFocused = false,
  isHeader = false,
  rowIndex = 0,
}) {
  if (isHeader) {
    return DATA_MANAGER_FROZEN_HEADER_CELL_CLASS_NAME;
  }

  if (isEditing || isFocused) {
    return "bg-slate-100";
  }

  if (hasDraft) {
    return "bg-amber-50";
  }

  return rowIndex % 2 === 0 ? "bg-slate-100" : "bg-white";
}

export function resolveFrozenColumnLabels(frozenColumnKeys = [], freezableColumnDescriptors = []) {
  const labelByKey = new Map(
    freezableColumnDescriptors.map((column) => [column.key, column.label]),
  );

  return frozenColumnKeys
    .map((columnKey) => labelByKey.get(columnKey) || columnKey)
    .filter(Boolean);
}

export function isColumnFrozen(columnKey, frozenColumnKeys = []) {
  const normalizedKey = typeof columnKey === "string" ? columnKey.trim() : "";
  return normalizedKey ? frozenColumnKeys.includes(normalizedKey) : false;
}
